import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    calculateNextReview,
    getQualityScore,
    getDueCards,
    sortCardsByPriority,
    getDeckStats,
    saveCardsProgress,
    loadCardsProgress,
    getCardsForStudy,
    saveCardsAfterStudy,
    loadDeckCards,
    saveDeckCards
} from '../services/spacedRepetition';
import { API_URL } from '../config';
import './Flashcards.css';

const FlashcardsEnhanced = () => {
    const [flashcards, setFlashcards] = useState([]);
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    const [loading, setLoading] = useState(false);
    const [sessionComplete, setSessionComplete] = useState(false);
    const [stats, setStats] = useState({
        again: 0,
        hard: 0,
        good: 0,
        easy: 0
    });

    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Support both old format (modules) and new format (deckMode)
    const { topic, modules, level, moduleId, moduleTitle, deckMode = 'legacy' } = location.state || {};

    useEffect(() => {
        if (!topic) {
            navigate('/flashcards');
            return;
        }
        if (currentUser) {
            loadFlashcards();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadFlashcards = async (forceRegenerate = false) => {
        setLoading(true);
        try {
            let cards = [];

            if (deckMode === 'level' && level) {
                // Load specific level deck
                cards = !forceRegenerate ? loadDeckCards(currentUser.uid, topic, level) : null;

                if (!cards || cards.length === 0) {
                    // Generate cards for this level - should have been done when lesson completed
                    console.log('No cards for level', level, '- cards should be generated when lesson completes');
                    setFlashcards([]);
                    setLoading(false);
                    return;
                }
            } else if (deckMode === 'master' || deckMode === 'all') {
                // Load all cards across all decks
                cards = getCardsForStudy(currentUser.uid, topic, deckMode);
            } else {
                // Legacy mode - use old storage
                cards = !forceRegenerate ? loadCardsProgress(currentUser.uid, topic) : null;

                if ((!cards || cards.length === 0) && modules) {
                    // Generate flashcards based on learning path modules (legacy behavior)
                    console.log('🎴 Generating flashcards from learning path modules...');

                    const moduleContext = modules.map(m => ({
                        title: m.title,
                        concepts: m.concepts,
                        description: m.description
                    }));

                    const response = await fetch(`${API_URL}/api/generate-flashcards`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            topic: topic,
                            count: 20,
                            moduleContext: moduleContext
                        })
                    });
                    const data = await response.json();

                    cards = data.map((card, index) => ({
                        ...card,
                        id: `${topic}_${index}_${Date.now()}`,
                        easinessFactor: 2.5,
                        repetitions: 0,
                        interval: 0,
                        nextReviewDate: null,
                        lastReviewedDate: null,
                        totalReviews: 0
                    }));

                    await saveCardsProgress(currentUser.uid, topic, cards);
                }
            }

            if (cards && cards.length > 0) {
                const dueCards = getDueCards(cards);
                const sortedCards = sortCardsByPriority(dueCards.length > 0 ? dueCards : cards);
                setFlashcards(sortedCards);
            } else {
                setFlashcards([]);
            }
        } catch (error) {
            console.error('Error loading flashcards:', error);
            alert('Failed to load flashcards. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRegenerateCards = () => {
        if (window.confirm('Are you sure? This will delete your current progress and generate new flashcards.')) {
            loadFlashcards(true);
            setCurrentCardIndex(0);
            setIsFlipped(false);
        }
    };

    const handleResponse = async (response) => {
        const qualityScore = getQualityScore(response);
        const currentCard = flashcards[currentCardIndex];

        // Update card using spaced repetition algorithm
        const updatedCard = calculateNextReview(currentCard, qualityScore);

        // Update flashcards array
        const updatedCards = [...flashcards];
        updatedCards[currentCardIndex] = updatedCard;
        setFlashcards(updatedCards);

        // Save progress based on mode
        if (deckMode === 'level' && level) {
            await saveDeckCards(currentUser.uid, topic, level, updatedCards);
        } else if (deckMode === 'master' || deckMode === 'all') {
            await saveCardsAfterStudy(currentUser.uid, topic, updatedCards, deckMode);
        } else {
            await saveCardsProgress(currentUser.uid, topic, updatedCards);
        }

        // Update stats
        setStats(prev => ({
            ...prev,
            [response]: prev[response] + 1
        }));

        // Move to next card or complete session
        if (currentCardIndex < flashcards.length - 1) {
            setTimeout(() => {
                setCurrentCardIndex(currentCardIndex + 1);
                setIsFlipped(false);
            }, 300);
        } else {
            setSessionComplete(true);
        }
    };

    const handleFlip = (e) => {
        e?.stopPropagation();
        setIsFlipped(prev => !prev);
    };

    const handleKeyPress = useCallback((e) => {
        if (e.key === ' ' && !sessionComplete) {
            e.preventDefault();
            setIsFlipped(prev => !prev);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionComplete]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [handleKeyPress]);

    const restartSession = () => {
        loadFlashcards();
        setCurrentCardIndex(0);
        setSessionComplete(false);
        setStats({ again: 0, hard: 0, good: 0, easy: 0 });
        setIsFlipped(false);
    };

    const getBackButtonText = () => {
        if (deckMode === 'level' || deckMode === 'master' || deckMode === 'all') {
            return '← Back to Decks';
        }
        return '← Back';
    };

    const handleBack = () => {
        if (deckMode === 'level' || deckMode === 'master' || deckMode === 'all') {
            navigate('/flashcard-decks', { state: { topic } });
        } else {
            navigate(-1);
        }
    };

    const getTitle = () => {
        if (deckMode === 'level' && moduleTitle) {
            return `Level ${level}: ${moduleTitle}`;
        }
        if (deckMode === 'master') {
            return `${topic} - Master Deck`;
        }
        if (deckMode === 'all') {
            return `${topic} - All Cards`;
        }
        return `${topic} - Spaced Repetition`;
    };

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
                <p>Loading your flashcards...</p>
            </div>
        );
    }

    if (!topic) {
        return (
            <div className="flashcard-container">
                <h2>No learning path selected</h2>
                <p>Please go back and select a learning path.</p>
                <button className="btn btn-primary" onClick={() => navigate('/flashcards')}>
                    Go to Flashcards
                </button>
            </div>
        );
    }

    if (flashcards.length === 0) {
        return (
            <div className="flashcard-container">
                <button className="btn btn-secondary" onClick={handleBack} style={{ marginBottom: '2rem' }}>
                    {getBackButtonText()}
                </button>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📝</div>
                    <h2>No Flashcards Yet</h2>
                    {deckMode === 'level' ? (
                        <p>Complete the lesson for Level {level} to generate flashcards!</p>
                    ) : (
                        <p>Complete lessons in your learning path to generate flashcards.</p>
                    )}
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate('/learning-path', { state: { topic } })}
                        style={{ marginTop: '1rem' }}
                    >
                        Go to Learning Path
                    </button>
                </div>
            </div>
        );
    }

    if (sessionComplete) {
        const deckStats = getDeckStats(flashcards);
        const totalReviewed = stats.again + stats.hard + stats.good + stats.easy;

        return (
            <div className="flashcard-container">
                <div className="session-complete">
                    <div className="complete-icon">🎉</div>
                    <h1>Session Complete!</h1>
                    <p className="subtitle">Great work! You've reviewed {totalReviewed} cards.</p>

                    <div className="session-stats">
                        <div className="stat-row">
                            <span className="stat-label">Again (Need to review)</span>
                            <span className="stat-value">{stats.again}</span>
                        </div>
                        <div className="stat-row">
                            <span className="stat-label">Hard</span>
                            <span className="stat-value">{stats.hard}</span>
                        </div>
                        <div className="stat-row">
                            <span className="stat-label">Good</span>
                            <span className="stat-value">{stats.good}</span>
                        </div>
                        <div className="stat-row">
                            <span className="stat-label">Easy</span>
                            <span className="stat-value">{stats.easy}</span>
                        </div>
                    </div>

                    <div className="deck-stats">
                        <h3>Deck Status</h3>
                        <div className="deck-stats-grid">
                            <div className="deck-stat-card">
                                <div className="stat-number">{deckStats.due}</div>
                                <div className="stat-description">Due Today</div>
                            </div>
                            <div className="deck-stat-card">
                                <div className="stat-number">{deckStats.new}</div>
                                <div className="stat-description">New Cards</div>
                            </div>
                            <div className="deck-stat-card">
                                <div className="stat-number">{deckStats.learning}</div>
                                <div className="stat-description">Learning</div>
                            </div>
                            <div className="deck-stat-card">
                                <div className="stat-number">{deckStats.mature}</div>
                                <div className="stat-description">Mastered</div>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button className="btn btn-primary btn-large" onClick={restartSession}>
                            Review More Cards
                        </button>
                        <button className="btn btn-secondary btn-large" onClick={handleBack}>
                            {getBackButtonText()}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const card = flashcards[currentCardIndex];
    const progress = ((currentCardIndex + 1) / flashcards.length) * 100;
    const dueToday = getDueCards(flashcards).length;

    return (
        <div className="flashcard-container">
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <button className="btn btn-secondary" onClick={handleBack}>
                    {getBackButtonText()}
                </button>
                {deckMode === 'legacy' && (
                    <button className="btn btn-secondary" onClick={handleRegenerateCards}>
                        🔄 Regenerate Cards
                    </button>
                )}
            </div>
            <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
                <h2>{getTitle()}</h2>
                <p>Card {currentCardIndex + 1} of {flashcards.length} • {dueToday} due today</p>
            </div>

            <div className="progress-bar" style={{ marginBottom: '2rem' }}>
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>

            <div className="flashcard-wrapper" onClick={handleFlip}>
                <div className={`flashcard-content ${isFlipped ? 'is-flipped' : ''}`}>
                    <div className="flashcard-front">
                        <div className="card-inner">
                            <h3>{card.front}</h3>
                            {!isFlipped && card.hint && (
                                <p style={{
                                    position: 'absolute',
                                    bottom: '1rem',
                                    color: 'var(--text-secondary)',
                                    fontSize: '0.9rem'
                                }}>
                                    💡 Hint: {card.hint}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flashcard-back-side">
                        <div className="card-inner">
                            <p>{card.back}</p>
                            {card.totalReviews > 0 && (
                                <div style={{
                                    position: 'absolute',
                                    bottom: '1rem',
                                    fontSize: '0.8rem',
                                    color: 'var(--text-secondary)'
                                }}>
                                    Reviewed {card.totalReviews} times • Next in {card.interval} days
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: '1rem', marginBottom: '2rem' }}>
                <p style={{ color: 'var(--text-secondary)' }}>
                    Click card or press SPACE to flip
                </p>
            </div>

            {isFlipped && (
                <div className="spaced-repetition-buttons">
                    <button
                        className="btn sr-btn sr-again"
                        onClick={() => handleResponse('again')}
                        title="Show again soon"
                    >
                        <span className="sr-time">&lt;10min</span>
                        <span className="sr-label">Again</span>
                    </button>
                    <button
                        className="btn sr-btn sr-hard"
                        onClick={() => handleResponse('hard')}
                        title="Show in a few days"
                    >
                        <span className="sr-time">
                            {card.interval < 1 ? '1d' : `${Math.round(card.interval * 0.8)}d`}
                        </span>
                        <span className="sr-label">Hard</span>
                    </button>
                    <button
                        className="btn sr-btn sr-good"
                        onClick={() => handleResponse('good')}
                        title="Standard interval"
                    >
                        <span className="sr-time">
                            {card.repetitions === 0 ? '1d' : card.repetitions === 1 ? '6d' : `${Math.round(card.interval * card.easinessFactor)}d`}
                        </span>
                        <span className="sr-label">Good</span>
                    </button>
                    <button
                        className="btn sr-btn sr-easy"
                        onClick={() => handleResponse('easy')}
                        title="Show much later"
                    >
                        <span className="sr-time">
                            {Math.round((card.interval || 1) * (card.easinessFactor + 0.5))}d
                        </span>
                        <span className="sr-label">Easy</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default FlashcardsEnhanced;
